/**
 * UserTable Component
 * Displays list of all users with sorting, searching, and row actions
 * Features: email, displayName, role, status, createdAt, lastLogin columns
 * Actions: Edit, Enable/Disable, Delete
 *
 * @module components/admin/UserTable
 */

import React, { useState, useEffect, useMemo } from 'react';
import { User } from '@/types';
import { logger } from '@/utils/logger';
import Button from '@/components/common/Button';

// ============================================================================
// TYPES
// ============================================================================

interface UserTableProps {
  users: User[];
  isLoading?: boolean;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onEnableDisable: (user: User, enable: boolean) => void;
  deletingUserId?: string;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

type SortField = 'email' | 'displayName' | 'role' | 'createdAt' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

// ============================================================================
// SKELETON ROWS
// ============================================================================

/**
 * Skeleton loading rows for table
 */
const SkeletonRow: React.FC = () => (
  <tr className="border-b border-walrus-gray hover:bg-walrus-charcoal/50 animate-pulse">
    <td className="px-6 py-4">
      <div className="h-4 bg-walrus-gray rounded w-32"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-walrus-gray rounded w-24"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-walrus-gray rounded w-20"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-6 bg-walrus-gray rounded w-16"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-walrus-gray rounded w-24"></div>
    </td>
    <td className="px-6 py-4">
      <div className="h-4 bg-walrus-gray rounded w-24"></div>
    </td>
    <td className="px-6 py-4">
      <div className="flex gap-2">
        <div className="h-8 bg-walrus-gray rounded w-16"></div>
        <div className="h-8 bg-walrus-gray rounded w-16"></div>
      </div>
    </td>
  </tr>
);

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * User table component
 * Displays all users with sorting, filtering, and action buttons
 *
 * @component
 * @example
 * <UserTable
 *   users={users}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 *   onEnableDisable={handleEnableDisable}
 * />
 */
const UserTable: React.FC<UserTableProps> = ({
  users,
  isLoading = false,
  onEdit,
  onDelete,
  onEnableDisable,
  deletingUserId,
  searchQuery = '',
  onSearchChange,
}) => {
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Debug logging
  useEffect(() => {
    logger.debug('[WALRUS] UserTable: rendered', {
      userCount: users.length,
      isLoading,
      sortField,
      sortDirection,
    });
  }, [users.length, isLoading, sortField, sortDirection]);

  /**
   * Handle column header click for sorting
   */
  const handleSort = (field: SortField) => {
    logger.debug('[WALRUS] UserTable: sorting by', { field });

    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to asc
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Filter and sort users
   */
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = users.filter(
        (user) =>
          user.email.toLowerCase().includes(query) ||
          (user.displayName && user.displayName.toLowerCase().includes(query))
      );
      logger.debug('[WALRUS] UserTable: filtered users by search', {
        query,
        originalCount: users.length,
        filteredCount: filtered.length,
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'displayName':
          aValue = a.displayName || '';
          bValue = b.displayName || '';
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'lastLogin':
          aValue = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          bValue = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [users, searchQuery, sortField, sortDirection]);

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Get sort indicator icon
   */
  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  /**
   * Render column header with sort button
   */
  const renderSortableHeader = (field: SortField, label: string) => (
    <button
      onClick={() => handleSort(field)}
      className="hover:text-walrus-silver transition-colors text-left font-semibold"
      title={`Sort by ${label}`}
    >
      {label}
      <span className="text-walrus-silver text-xs">{getSortIndicator(field)}</span>
    </button>
  );

  // Render empty state
  if (!isLoading && users.length === 0) {
    return (
      <div className="rounded-lg border border-walrus-gray bg-walrus-charcoal/50 px-8 py-16 text-center">
        <svg
          className="mx-auto mb-4 h-12 w-12 text-walrus-gray"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 4.354a4 4 0 110 5.292M15 21H3.914a.97.97 0 01-.945-1.167l2.697-10.788A2 2 0 015.734 8h12.532a2 2 0 011.946 1.445l2.697 10.788a.97.97 0 01-.945 1.167M15 21h6m0 0h.01"
          />
        </svg>
        <h3 className="mb-2 text-lg font-medium text-walrus-white">No users found</h3>
        <p className="text-walrus-silver">Start by inviting the first user to the system.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search bar */}
      {onSearchChange && (
        <div className="flex gap-3">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-walrus-silver"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search users by email or name..."
              value={searchQuery}
              onChange={(e) => {
                logger.debug('[WALRUS] UserTable: search query changed', { query: e.target.value });
                onSearchChange(e.target.value);
              }}
              className="w-full rounded-lg border border-walrus-gray bg-walrus-charcoal pl-10 pr-4 py-2 text-walrus-white placeholder-walrus-silver transition-colors focus:border-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-walrus-gray bg-walrus-charcoal">
        <table className="w-full">
          <thead>
            <tr className="border-b border-walrus-gray bg-walrus-charcoal/50">
              <th className="px-6 py-4 text-left text-walrus-white">
                {renderSortableHeader('email', 'Email')}
              </th>
              <th className="px-6 py-4 text-left text-walrus-white">
                {renderSortableHeader('displayName', 'Display Name')}
              </th>
              <th className="px-6 py-4 text-left text-walrus-white">
                {renderSortableHeader('role', 'Role')}
              </th>
              <th className="px-6 py-4 text-left text-walrus-white">
                {renderSortableHeader('createdAt', 'Created')}
              </th>
              <th className="px-6 py-4 text-left text-walrus-white">
                {renderSortableHeader('lastLogin', 'Last Login')}
              </th>
              <th className="px-6 py-4 text-left text-walrus-white">Status</th>
              <th className="px-6 py-4 text-left text-walrus-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              // Loading skeleton rows
              Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={`skeleton-${i}`} />)
            ) : (
              // Data rows
              filteredAndSortedUsers.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-walrus-gray transition-colors hover:bg-walrus-charcoal/50"
                >
                  {/* Email */}
                  <td className="px-6 py-4 text-walrus-white font-medium" title={user.email}>
                    <span className="truncate">{user.email}</span>
                  </td>

                  {/* Display Name */}
                  <td className="px-6 py-4 text-walrus-silver">
                    {user.displayName || <span className="italic text-walrus-gray">Not set</span>}
                  </td>

                  {/* Role */}
                  <td className="px-6 py-4">
                    <span
                      className={`inline-block rounded px-3 py-1 text-sm font-medium ${
                        user.role === 'admin'
                          ? 'bg-blue-900/50 text-blue-200'
                          : 'bg-walrus-gray/50 text-walrus-silver'
                      }`}
                    >
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </span>
                  </td>

                  {/* Created At */}
                  <td className="px-6 py-4 text-walrus-silver text-sm">{formatDate(user.createdAt)}</td>

                  {/* Last Login */}
                  <td className="px-6 py-4 text-walrus-silver text-sm">
                    {user.lastLogin ? formatDate(user.lastLogin) : <span className="text-walrus-gray italic">Never</span>}
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    <span
                      className={`inline-block rounded px-3 py-1 text-sm font-medium ${
                        user.isEnabled
                          ? 'bg-emerald-900/50 text-emerald-200'
                          : 'bg-red-900/50 text-red-200'
                      }`}
                    >
                      {user.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {/* Edit button */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          logger.debug('[WALRUS] UserTable: edit clicked', { userId: user.id });
                          onEdit(user);
                        }}
                      >
                        Edit
                      </Button>

                      {/* Enable/Disable button */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          logger.debug('[WALRUS] UserTable: toggle enable/disable', {
                            userId: user.id,
                            currentState: user.isEnabled,
                          });
                          onEnableDisable(user, !user.isEnabled);
                        }}
                      >
                        {user.isEnabled ? 'Disable' : 'Enable'}
                      </Button>

                      {/* Delete button */}
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deletingUserId === user.id}
                        onClick={() => {
                          logger.debug('[WALRUS] UserTable: delete clicked', { userId: user.id });
                          onDelete(user);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Results info */}
      {!isLoading && users.length > 0 && (
        <div className="text-sm text-walrus-silver">
          Showing {filteredAndSortedUsers.length} of {users.length} users
          {searchQuery && ` (filtered by "${searchQuery}")`}
        </div>
      )}
    </div>
  );
};

UserTable.displayName = 'UserTable';

export default UserTable;
